import { useCallback, useEffect, useMemo, useState, type RefObject } from "react"
import type { LibraryCrumb } from "@/components/library/library-breadcrumbs"
import {
  LIBRARY_ITEM_ACTION_ATTR,
  LIBRARY_ITEM_ATTR,
  type LibraryItem,
  type LibraryItemKind,
} from "@/components/library/library-item"
import { filterLibraryItems, type LibraryKindFilterValue } from "@/components/library/library-kind-filter"
import { LIBRARY_TREE_ITEM_ATTR } from "@/components/library/library-tree"
import { useTranslation } from "@/hooks/use-translation"
import {
  EMPTY_LIBRARY_ENTITIES,
  EMPTY_LIBRARY_FLOWS,
  isLibraryFlowOrphan,
  libraryFlowBoards,
  type LibraryCanvasRow,
  type LibraryEntitySections,
  type LibraryFlowRow,
  type LibraryTree as LibraryTreeData,
  type LibraryTreeEpisode,
  type LibraryTreeScene,
  type LibraryTreeSeason,
  type LibraryTreeShot,
} from "@/lib/library-api"
import { insideDialogLayer } from "@/lib/dialog-layer"
import {
  activateLibraryItem,
  firstLibraryItemKey,
  focusLibraryItem,
  hasLibraryItem,
  moveLibraryFocus,
} from "@/lib/library-focus"
import { LIBRARY_SORT_DEFAULT, sortLibraryItems, toggleLibrarySort, type LibrarySortColumn } from "@/lib/library-sort"
import { syncLibraryUrl, type LibrarySelection, type LibraryShotsNode } from "@/lib/library-url"
import { useLibraryViewMode } from "@/lib/library-view-mode"

type EpisodePath = { season: LibraryTreeSeason; episode: LibraryTreeEpisode }
type ScenePath = EpisodePath & { scene: LibraryTreeScene }
type ShotPath = ScenePath & { shot: LibraryTreeShot }

export function findEpisodePath(tree: LibraryTreeData, episodeId: number): EpisodePath | null {
  for (const season of tree.seasons) {
    const episode = season.episodes.find((ep) => ep.id === episodeId)
    if (episode) return { season, episode }
  }
  return null
}

export function findScenePath(tree: LibraryTreeData, sceneId: number): ScenePath | null {
  for (const season of tree.seasons) {
    for (const episode of season.episodes) {
      const scene = episode.scenes.find((sc) => sc.id === sceneId)
      if (scene) return { season, episode, scene }
    }
  }
  return null
}

export function findShotPath(tree: LibraryTreeData, shotId: number): ShotPath | null {
  for (const season of tree.seasons) {
    for (const episode of season.episodes) {
      for (const scene of episode.scenes) {
        const shot = scene.shots.find((sh) => sh.id === shotId)
        if (shot) return { season, episode, scene, shot }
      }
    }
  }
  return null
}

/** Дефолтная папка секции «Шоты»: активный эпизод проекта, иначе первый. */
export function defaultShotsNode(tree: LibraryTreeData, activeEpisodeId: number | null): LibraryShotsNode | null {
  const episodes = tree.seasons.flatMap((season) => season.episodes)
  if (episodes.length === 0) return null
  const episodeId =
    activeEpisodeId != null && episodes.some((ep) => ep.id === activeEpisodeId) ? activeEpisodeId : episodes[0].id
  return { level: "episode", episodeId }
}

/** Стабильный ключ папки — сравнение выбранного узла со списком папок с ассетами. */
function folderKey(selection: LibrarySelection): string {
  if (selection.section === "canvas") return `c${selection.canvasId ?? ""}`
  if (selection.section === "flow") {
    if (selection.orphans) return "flow-orphans"
    return `f${selection.flowId ?? ""}`
  }
  if (selection.section === "sketches") return "sketches"
  if (selection.section === "uploads") return "uploads"
  if (selection.section !== "shots") return `${selection.section}:${selection.entityId ?? ""}`
  const node = selection.node
  if (node?.level === "shot") return `sh${node.shotId}`
  if (node?.level === "scene") return `s${node.sceneId}`
  if (node?.level === "animatic") return `an${node.episodeId}`
  if (node?.level === "episode") return `e${node.episodeId}`
  return "shots"
}

export function normalizeLibrarySelection(
  parsed: LibrarySelection | null,
  tree: LibraryTreeData,
  canvases: LibraryCanvasRow[],
  activeEpisodeId: number | null,
  entities: LibraryEntitySections = EMPTY_LIBRARY_ENTITIES,
  flows: LibraryFlowRow[] = EMPTY_LIBRARY_FLOWS,
): LibrarySelection {
  if (!parsed) return { section: "shots", node: defaultShotsNode(tree, activeEpisodeId) }
  if (parsed.section === "shots" && parsed.node == null) {
    return { section: "shots", node: defaultShotsNode(tree, activeEpisodeId) }
  }
  if (parsed.section === "canvas" && parsed.canvasId == null) {
    return { section: "canvas", canvasId: canvases[0]?.id ?? null }
  }
  if (parsed.section === "flow") {
    if (parsed.orphans && flows.some(isLibraryFlowOrphan)) {
      return { section: "flow", flowId: null, orphans: true }
    }
    const boards = libraryFlowBoards(flows)
    const known = parsed.flowId != null && boards.some((row) => row.id === parsed.flowId)
    return { section: "flow", flowId: known ? parsed.flowId : (boards[0]?.id ?? null) }
  }
  if (parsed.section === "sketches") {
    return { section: "sketches" }
  }
  if (parsed.section === "uploads") {
    return { section: "uploads" }
  }
  // Ссылка на секцию без сущности открывает первую в списке — как у Холста.
  if (parsed.section !== "shots" && parsed.section !== "canvas" && parsed.entityId == null) {
    return { section: parsed.section, entityId: entities[parsed.section][0]?.id ?? null }
  }
  return parsed
}

export type LibraryBrowserOptions = {
  tree: LibraryTreeData
  canvases: LibraryCanvasRow[]
  flows?: LibraryFlowRow[]
  /** Герои, локации и реквизит: нужны для крошек и дефолтной сущности в секции. */
  entities?: LibraryEntitySections
  activeEpisodeId: number | null
  /** Стартовая папка; страница берёт её из query, модалка — из текущего шота. */
  initialSelection: LibrarySelection
  /** Только страница канонизирует query при смене папки — модалка не трогает адрес. */
  persistUrl?: boolean
  /** Корень браузера (дерево + содержимое): по нему отличается «клик мимо» от клика за пределами. */
  rootRef: RefObject<HTMLElement | null>
  /** Контейнер содержимого папки: в нём живут плитки, по нему работают курсор и просмотрщик. */
  contentRef: RefObject<HTMLElement | null>
}

/**
 * Ядро браузера Библиотеки: выбранная папка, курсор клавиатуры по двум зонам
 * (дерево ↔ содержимое), плоский список ассетов открытой папки и просмотрщик
 * с перелистыванием. Общее для страницы раздела и модалки выбора кадра —
 * различие сводится к persistUrl.
 */
export function useLibraryBrowser({
  tree,
  canvases,
  flows = EMPTY_LIBRARY_FLOWS,
  entities = EMPTY_LIBRARY_ENTITIES,
  activeEpisodeId,
  initialSelection,
  persistUrl = false,
  rootRef,
  contentRef,
}: LibraryBrowserOptions) {
  const { t, locale } = useTranslation(["workspace", "common"])
  const [viewMode, setViewMode] = useLibraryViewMode()
  const [selection, setSelection] = useState<LibrarySelection>(initialSelection)

  // null — курсора нет: клик мимо элементов снимает выделение, навигация вернётся с новым кликом.
  const [focusZone, setFocusZone] = useState<"tree" | "content" | null>("tree")
  const [focusedKey, setFocusedKey] = useState<string | null>(null)
  // Кольцо курсора видно только после клавиатуры. Клик помнит строку для следующих стрелок, но не подсвечивает её.
  const [keyboardCursor, setKeyboardCursor] = useState(false)
  const [items, setItems] = useState<LibraryItem[]>([])
  const [kindFilter, setKindFilter] = useState<LibraryKindFilterValue>("all")
  const [sort, setSort] = useState(LIBRARY_SORT_DEFAULT)
  const [assetKey, setAssetKey] = useState<string | null>(null)
  const toggleSort = useCallback((column: LibrarySortColumn) => {
    setSort((previous) => toggleLibrarySort(previous, column))
  }, [])

  // Фильтр не сбрасывается при переходе между папками намеренно: «покажи только видео»
  // — это режим осмотра проекта, а не свойство одной папки. Сортировка — тоже: выбранная
  // колонка живёт, пока открыт браузер. Видимый список отличается от items и потому
  // считается здесь: по нему идут просмотрщик и поиск элемента под курсором, тогда как
  // счётчики чипов обязаны видеть папку целиком.
  const typeLabel = useMemo(
    (): Record<LibraryItemKind, string> => ({
      folder: t("library.types.folder"),
      image: t("library.types.image"),
      video: t("library.types.video"),
      audio: t("library.types.audio"),
    }),
    [t],
  )
  const visibleItems = useMemo(
    () => sortLibraryItems(filterLibraryItems(items, kindFilter), sort, { locale, typeLabel }),
    [items, kindFilter, sort, locale, typeLabel],
  )

  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement | null
      if (!target) return
      setKeyboardCursor(false)
      const onItem = target.closest(
        `[${LIBRARY_ITEM_ATTR}], [${LIBRARY_TREE_ITEM_ATTR}], [${LIBRARY_ITEM_ACTION_ATTR}]`,
      )
      if (onItem) return
      const root = rootRef.current
      // Внутри браузера клик мимо элементов снимает курсор. Снаружи курсор держится,
      // если клик пришёлся на диалог: просмотрщик ассета и футер пикера открыты поверх
      // и ронять выделение не должны. Затемнение просмотрщика — тот же слой: закрытие
      // по backdrop не выбор отменяет, а всего лишь закрывает просмотр.
      if (!(root && root.contains(target)) && insideDialogLayer(target)) return
      setFocusZone(null)
      setFocusedKey(null)
    }
    document.addEventListener("pointerdown", onPointerDown, true)
    return () => document.removeEventListener("pointerdown", onPointerDown, true)
  }, [rootRef])

  // Канонизируем query при заходе и после каждой смены папки (replaceState).
  useEffect(() => {
    if (persistUrl) syncLibraryUrl(selection)
  }, [selection, persistUrl])

  const select = useCallback((next: LibrarySelection) => setSelection(next), [])

  // Перелистывание — по всем ассетам открытой папки. Групп в списке нет: всё на виду.
  const assetItems = useMemo(() => visibleItems.filter((item) => item.asset), [visibleItems])
  const assetIndex = assetKey != null ? assetItems.findIndex((item) => item.key === assetKey) : -1

  /** Путь до открытой папки — идёт в заголовок просмотрщика перед именем ассета. */
  const folderPath = useMemo<string[]>(() => {
    if (selection.section === "canvas") {
      const board = canvases.find((b) => b.id === selection.canvasId)
      return [t("library.sections.canvas"), board?.title].filter((part): part is string => Boolean(part))
    }
    if (selection.section === "flow") {
      if (selection.orphans) {
        const row = flows.find(isLibraryFlowOrphan)
        return [t("library.sections.flow"), row?.title].filter((part): part is string => Boolean(part))
      }
      const board = libraryFlowBoards(flows).find((row) => row.id === selection.flowId)
      return [t("library.sections.flow"), board?.title].filter((part): part is string => Boolean(part))
    }
    if (selection.section === "sketches") {
      return [t("library.sections.sketches")]
    }
    if (selection.section === "uploads") {
      return [t("library.sections.uploads")]
    }
    if (selection.section !== "shots") {
      const row = entities[selection.section].find((r) => r.id === selection.entityId)
      return [t(`library.sections.${selection.section}`), row?.name].filter((part): part is string => Boolean(part))
    }
    const node = selection.node
    if (!node) return []
    if (node.level === "episode") {
      const path = findEpisodePath(tree, node.episodeId)
      return path ? [`S${path.season.number}E${path.episode.number}`] : []
    }
    if (node.level === "animatic") {
      const path = findEpisodePath(tree, node.episodeId)
      if (!path) return [t("library.crumbs.animatic")]
      return [`S${path.season.number}E${path.episode.number}`, t("library.crumbs.animatic")]
    }
    if (node.level === "scene") {
      const path = findScenePath(tree, node.sceneId)
      if (!path) return []
      return [`S${path.season.number}E${path.episode.number}`, t("library.crumbs.scene", { number: path.scene.number })]
    }
    const path = findShotPath(tree, node.shotId)
    if (!path) return []
    return [
      `S${path.season.number}E${path.episode.number}`,
      t("library.crumbs.scene", { number: path.scene.number }),
      t("library.crumbs.shot", { number: `${path.scene.number}.${path.shot.number}` }),
    ]
  }, [selection, tree, canvases, flows, entities, t])

  // Заголовок ассета — вся иерархия одной строкой: «S1E1 · Сц. 1 · Шот 1.3 · v3 · Первый кадр».
  const currentAsset = useMemo(() => {
    const asset = assetIndex >= 0 ? assetItems[assetIndex].asset : null
    if (!asset) return null
    if (folderPath.length === 0) return asset
    return { ...asset, title: [...folderPath, asset.title].join(" · ") }
  }, [assetIndex, assetItems, folderPath])

  // Соседние кадры кладём в кэш браузера заранее: при перелистывании картинка
  // появляется сразу, без пустого кадра между ассетами.
  useEffect(() => {
    if (assetIndex < 0) return
    for (const offset of [1, -1]) {
      const neighbour = assetItems[assetIndex + offset]?.asset
      if (neighbour?.kind !== "image" || !neighbour.url) continue
      const image = new Image()
      image.src = neighbour.url
    }
  }, [assetIndex, assetItems])

  const stepAsset = useCallback(
    (direction: 1 | -1) => {
      if (assetIndex < 0) return
      const next = assetIndex + direction
      if (next < 0 || next >= assetItems.length) return
      setAssetKey(assetItems[next].key)
      setFocusedKey(assetItems[next].key)
      setKeyboardCursor(true)
    },
    [assetIndex, assetItems],
  )

  // Папка сменилась — закрываем просмотрщик, иначе он показывал бы чужой ассет.
  const selectionKeyValue = folderKey(selection)
  useEffect(() => {
    setAssetKey(null)
  }, [selectionKeyValue])

  // Ассет исчез из папки, не сменив её: удалили плитку, перечиталась лента. Просмотрщик
  // закрывается сам (currentAsset становится null), а вот assetKey остался бы взведён —
  // и глушил бы стрелки и Enter в содержимом до следующей смены папки.
  useEffect(() => {
    if (assetKey != null && assetIndex < 0) setAssetKey(null)
  }, [assetKey, assetIndex])

  /** Открыть ассет в просмотрщике. Курсор встаёт на него; отметка чекбокса не меняется. */
  const openAsset = useCallback((item: LibraryItem) => {
    if (!item.asset) return
    setFocusZone("content")
    setFocusedKey(item.key)
    setAssetKey(item.key)
  }, [])

  const handleOpenItem = useCallback(
    (item: LibraryItem) => {
      setFocusedKey(item.key)
      // Ассет открывается и на странице, и в пикере. Папка проваливается внутрь.
      if (item.asset) openAsset(item)
      else item.onOpen?.()
    },
    [openAsset],
  )

  const handleItemsChange = useCallback((next: LibraryItem[]) => {
    setItems(next)
  }, [])

  // Содержимое сменилось (провалились в папку) — курсор мог указывать на исчезнувший
  // элемент: тогда фокус уходил на body и стрелки замолкали до нового клика.
  // Пока открыт просмотрщик, курсор ведёт перелистывание и сбрасывать его на первый
  // элемент нельзя, иначе после закрытия он окажется не на том кадре.
  useEffect(() => {
    if (focusZone !== "content" || assetKey != null) return
    const container = contentRef.current
    const active = document.activeElement
    const hasFocused = hasLibraryItem(container, focusedKey)
    // Чекбокс строки фокусируется раньше click и сам атрибута элемента не несёт.
    // Сбрасывать курсор на первый ассет нельзя: focus() на чужую кнопку срывает клик.
    // Мышь курсор не рисует, поэтому ключ здесь не переносим.
    const actionHost =
      active instanceof HTMLElement ? active.closest<HTMLElement>(`[${LIBRARY_ITEM_ACTION_ATTR}]`) : null
    if (actionHost) return
    if (hasFocused) return
    // Плитка уже под фокусом браузера — значит, идёт клик по ней, и курсор назовёт его
    // onClick: focus у кнопки случается РАНЬШЕ click, и между ними есть рендер, где зона
    // уже "content", а ключа ещё нет. Подставляя здесь первый элемент папки, мы на кадр
    // подсвечивали заголовок группы, а следующий click перебрасывал выделение на саму
    // плитку — то самое мигание. Курсор, потерянный по-настоящему (элемент исчез, фокус
    // ушёл на body), сюда по-прежнему попадает.
    const activeIsItem = active instanceof HTMLElement && active.hasAttribute(LIBRARY_ITEM_ATTR)
    if (activeIsItem) return
    const key = firstLibraryItemKey(container)
    setFocusedKey(key)
    focusLibraryItem(container, key)
  }, [items, focusZone, focusedKey, assetKey, contentRef])

  /** Enter в дереве — проваливаемся в содержимое папки, всегда на первый элемент. */
  const focusContent = useCallback(() => {
    setFocusZone("content")
    setKeyboardCursor(true)
    const key = firstLibraryItemKey(contentRef.current)
    setFocusedKey(key)
    requestAnimationFrame(() => focusLibraryItem(contentRef.current, key))
  }, [contentRef])

  /**
   * Просмотрщик закрылся — курсор встаёт на плитку последнего показанного ассета
   * (после перелистывания это уже не та, с которой открывали), чтобы стрелки шли дальше.
   */
  const restoreContentFocus = useCallback(() => {
    setFocusZone("content")
    const container = contentRef.current
    // Плитки уже нет в DOM — встаём на первую.
    const key = hasLibraryItem(container, focusedKey) ? focusedKey : firstLibraryItemKey(container)
    setFocusedKey(key)
    requestAnimationFrame(() => focusLibraryItem(contentRef.current, key))
  }, [focusedKey, contentRef])

  const closeAsset = useCallback(() => setAssetKey(null), [])

  const handleContentKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.metaKey || e.ctrlKey || e.altKey || assetKey != null) return
    const container = contentRef.current
    if (!container) return

    if (e.key === "Escape") {
      e.preventDefault()
      // В диалоге Escape дошёл бы до Radix и закрыл пикер вместе с выбором —
      // первый Escape только возвращает курсор в дерево.
      e.stopPropagation()
      setKeyboardCursor(false)
      setFocusZone("tree")
      return
    }
    if (e.key === "Enter") {
      e.preventDefault()
      setKeyboardCursor(true)
      // Раскрытие группы перерисовывает блок — возвращаем курсор на тот же элемент,
      // иначе фокус уезжает на соседний.
      const key = focusedKey
      requestAnimationFrame(() => focusLibraryItem(contentRef.current, key))
      // Клик по самому элементу: ассет открывается, папка проваливается внутрь.
      activateLibraryItem(container, focusedKey)
      return
    }

    const move =
      e.key === "ArrowRight"
        ? "next"
        : e.key === "ArrowLeft"
          ? "prev"
          : e.key === "ArrowDown"
            ? "down"
            : e.key === "ArrowUp"
              ? "up"
              : e.key === "Home"
                ? "first"
                : e.key === "End"
                  ? "last"
                  : null
    if (!move) return
    e.preventDefault()
    setKeyboardCursor(true)
    const target = moveLibraryFocus(container, focusedKey, move)
    if (!target) return
    setFocusedKey(target)
    focusLibraryItem(container, target)
  }

  const crumbs = useMemo<LibraryCrumb[]>(() => {
    if (selection.section === "shots") {
      const node = selection.node
      const rootCrumb: LibraryCrumb = {
        key: "shots",
        label: t("library.sections.shots"),
        onClick:
          node != null ? () => select({ section: "shots", node: defaultShotsNode(tree, activeEpisodeId) }) : undefined,
      }
      if (!node) return [rootCrumb]
      if (node.level === "episode") {
        const path = findEpisodePath(tree, node.episodeId)
        const label = path ? `S${path.season.number}E${path.episode.number}` : `#${node.episodeId}`
        return [rootCrumb, { key: `e${node.episodeId}`, label }]
      }
      if (node.level === "animatic") {
        const path = findEpisodePath(tree, node.episodeId)
        const episodeLabel = path ? `S${path.season.number}E${path.episode.number}` : `#${node.episodeId}`
        return [
          rootCrumb,
          {
            key: `e${node.episodeId}`,
            label: episodeLabel,
            onClick: () => select({ section: "shots", node: { level: "episode", episodeId: node.episodeId } }),
          },
          { key: `an${node.episodeId}`, label: t("library.crumbs.animatic") },
        ]
      }
      if (node.level === "scene") {
        const path = findScenePath(tree, node.sceneId)
        if (!path) return [rootCrumb, { key: `s${node.sceneId}`, label: `#${node.sceneId}` }]
        return [
          rootCrumb,
          {
            key: `e${path.episode.id}`,
            label: `S${path.season.number}E${path.episode.number}`,
            onClick: () => select({ section: "shots", node: { level: "episode", episodeId: path.episode.id } }),
          },
          { key: `s${node.sceneId}`, label: t("library.crumbs.scene", { number: path.scene.number }) },
        ]
      }
      const path = findShotPath(tree, node.shotId)
      if (!path) return [rootCrumb, { key: `sh${node.shotId}`, label: `#${node.shotId}` }]
      return [
        rootCrumb,
        {
          key: `e${path.episode.id}`,
          label: `S${path.season.number}E${path.episode.number}`,
          onClick: () => select({ section: "shots", node: { level: "episode", episodeId: path.episode.id } }),
        },
        {
          key: `s${path.scene.id}`,
          label: t("library.crumbs.scene", { number: path.scene.number }),
          onClick: () => select({ section: "shots", node: { level: "scene", sceneId: path.scene.id } }),
        },
        {
          key: `sh${node.shotId}`,
          label: t("library.crumbs.shot", { number: `${path.scene.number}.${path.shot.number}` }),
        },
      ]
    }
    if (selection.section === "sketches") {
      return [{ key: "sketches", label: t("library.sections.sketches") }]
    }
    if (selection.section === "uploads") {
      return [{ key: "uploads", label: t("library.sections.uploads") }]
    }
    if (selection.section === "flow") {
      const boards = libraryFlowBoards(flows)
      const rootCrumb: LibraryCrumb = {
        key: "flow",
        label: t("library.sections.flow"),
        onClick: () => select({ section: "flow", flowId: boards[0]?.id ?? null }),
      }
      if (selection.orphans) {
        const row = flows.find(isLibraryFlowOrphan)
        if (!row) return [rootCrumb]
        return [rootCrumb, { key: "flow-orphans", label: row.title }]
      }
      const board = boards.find((row) => row.id === selection.flowId)
      if (!board) return [rootCrumb]
      return [rootCrumb, { key: `f${board.id}`, label: board.title }]
    }
    if (selection.section !== "canvas") {
      const section = selection.section
      const rows = entities[section]
      const row = rows.find((r) => r.id === selection.entityId)
      const rootCrumb: LibraryCrumb = {
        key: section,
        label: t(`library.sections.${section}`),
        onClick: row != null ? () => select({ section, entityId: rows[0]?.id ?? null }) : undefined,
      }
      if (!row) return [rootCrumb]
      return [rootCrumb, { key: `${section}:${row.id}`, label: row.name ?? `#${row.id}` }]
    }
    const board = canvases.find((b) => b.id === selection.canvasId)
    if (!board) return [{ key: "canvas", label: t("library.sections.canvas") }]
    return [
      {
        key: "canvas",
        label: t("library.sections.canvas"),
        onClick: () => select({ section: "canvas", canvasId: canvases[0]?.id ?? null }),
      },
      { key: `c${board.id}`, label: board.title },
    ]
  }, [selection, tree, canvases, flows, entities, activeEpisodeId, select, t])

  return {
    selection,
    select,
    crumbs,
    folderPath,
    viewMode,
    setViewMode,
    focusZone,
    setFocusZone,
    focusedKey,
    setFocusedKey,
    keyboardCursor,
    items,
    kindFilter,
    setKindFilter,
    sort,
    toggleSort,
    handleItemsChange,
    handleOpenItem,
    openAsset,
    handleContentKeyDown,
    focusContent,
    restoreContentFocus,
    currentAsset,
    assetOpen: assetKey != null,
    /** Ключ ассета, открытого в просмотрщике. */
    assetKey,
    closeAsset,
    stepAsset,
    hasPrev: assetIndex > 0,
    hasNext: assetIndex >= 0 && assetIndex < assetItems.length - 1,
  }
}
